import {
  isAddressComplete,
  type MemberAddress,
} from "@/lib/queries/member-data";
import type { Dispatch, SetStateAction } from "react";
import { useEffect, useRef } from "react";

interface ProfileAddressLike {
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
  line1: string | null;
  line2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
  country: string | null;
  isPrimary: boolean;
}

/**
 * Auto-selects the primary profile address when the user has no existing
 * address set. Uses a ref guard to fire at most once per mount.
 */
export function useAutoSelectPrimaryAddress(
  profileAddresses: ProfileAddressLike[] | undefined,
  editedAddress: MemberAddress | null,
  initialAddress: MemberAddress | null,
  setEditedAddress: Dispatch<SetStateAction<MemberAddress | null>>,
): void {
  const hasAutoSelectedRef = useRef(false);

  useEffect(() => {
    if (hasAutoSelectedRef.current) return;
    if (editedAddress) return;
    if (initialAddress && isAddressComplete(initialAddress)) return;

    const primaryAddr = profileAddresses?.find((a) => a.isPrimary);
    if (primaryAddr) {
      hasAutoSelectedRef.current = true;
      setEditedAddress({
        firstName: primaryAddr.firstName || "",
        lastName: primaryAddr.lastName || "",
        address1: primaryAddr.line1 || "",
        address2: primaryAddr.line2 || "",
        city: primaryAddr.city || "",
        country: primaryAddr.country || "",
        province: primaryAddr.state || "",
        zip: primaryAddr.zip || "",
        phone: primaryAddr.phone || "",
      });
    }
  }, [profileAddresses, editedAddress, initialAddress, setEditedAddress]);
}
